import { useState } from "react";
import { toast } from "sonner";
import {
  getMockAssignments,
  assignmentOptions,
  assignmentsToCsv,
  assignmentsCsvFilename,
  getMockAssignmentUpload,
  type AssignmentUploadReview,
} from "@shared-core/mock-data/pricing-assignments";
import type { CustomerAssignment } from "@shared-core/data/pricing-admin-types";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { Card, CardContent } from "../app/components/primitives/card";
import { Button } from "../app/components/primitives/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../app/components/primitives/select";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../app/components/primitives/table";
import {
  Download,
  Upload,
  PencilLine,
  AlertTriangle,
  Check,
} from "lucide-react";

// Assignments — the customer → catalogue + price group join. The two columns
// move independently; every customer has both (defaulting to Standard). Bulk
// edits via a 3-column CSV download/upload, the upload going through a validated,
// revertible diff review (mirrors the catalogue bulk import). Mock-data backed.

export function AssignmentsPage() {
  const [rows, setRows] = useState<CustomerAssignment[]>(() =>
    getMockAssignments(),
  );
  const [review, setReview] = useState<AssignmentUploadReview | null>(null);
  const opts = assignmentOptions();
  const capabilities = usePlatformCapabilities();

  const setRow = (code: string, patch: Partial<CustomerAssignment>) =>
    setRows((rs) =>
      rs.map((r) => (r.customerCode === code ? { ...r, ...patch } : r)),
    );

  const download = () => {
    capabilities.downloadTextFile(
      assignmentsCsvFilename,
      assignmentsToCsv(rows),
      "text/csv",
    );
    toast.success(`Downloaded ${assignmentsCsvFilename}`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1>Assignments</h1>
        <p className="text-sm text-muted-foreground">
          Which catalogue and price group each customer is on. The two are
          independent — anyone unset uses Standard.
        </p>
      </div>

      {!review && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={download}>
            <Download className="size-4" /> Download CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setReview(getMockAssignmentUpload())}
          >
            <Upload className="size-4" /> Upload CSV
          </Button>
        </div>
      )}

      {review ? (
        <ReviewPanel
          review={review}
          onApply={() => {
            toast.success("Assignments updated", {
              description: `${review.changes.length} changed · ${review.unknownCustomers.length} skipped`,
            });
            setReview(null);
          }}
          onDiscard={() => setReview(null)}
        />
      ) : (
        <Card className="py-0">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Catalogue</TableHead>
                  <TableHead>Price group</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.customerCode}>
                    <TableCell className="font-medium">
                      {r.customerName}
                    </TableCell>
                    <TableCell>
                      <AssignSelect
                        value={r.catalogueId}
                        options={opts.catalogues}
                        onChange={(v) =>
                          setRow(r.customerCode, { catalogueId: v })
                        }
                        ariaLabel={`Catalogue for ${r.customerName}`}
                      />
                    </TableCell>
                    <TableCell>
                      <AssignSelect
                        value={r.priceGroupId}
                        options={opts.priceGroups}
                        onChange={(v) =>
                          setRow(r.customerCode, { priceGroupId: v })
                        }
                        ariaLabel={`Price group for ${r.customerName}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function AssignSelect({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  ariaLabel: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        size="sm"
        aria-label={ariaLabel}
        className="w-auto min-w-[12rem]"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ReviewPanel({
  review,
  onApply,
  onDiscard,
}: {
  review: AssignmentUploadReview;
  onApply: () => void;
  onDiscard: () => void;
}) {
  const hasSkips = review.unknownCustomers.length > 0;
  return (
    <div className="grid items-start gap-6 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Card className="py-4">
          <CardContent className="space-y-2">
            <div className="text-sm font-medium">
              Validation · {review.fileName}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="inline-flex items-center gap-1 text-green-700">
                <Check className="size-4" /> {review.rowsOk} rows ok
              </span>
              {hasSkips && (
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <AlertTriangle className="size-4" />{" "}
                  {review.unknownCustomers.length} unknown customer skipped
                </span>
              )}
            </div>
            {review.unknownCustomers.map((c) => (
              <div key={c} className="pl-5 text-xs text-muted-foreground">
                └ “{c}” isn't in the customer list — row skipped
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="py-4">
          <CardContent className="space-y-3">
            <div className="text-sm font-medium">
              Changes vs current · {review.changes.length} changed
            </div>
            <div className="divide-y divide-border">
              {review.changes.map((c, i) => (
                <div key={i} className="flex items-center gap-3 py-2 text-sm">
                  <PencilLine className="size-4 shrink-0 text-amber-600" />
                  <span className="w-44 shrink-0 font-medium">
                    {c.customer}
                  </span>
                  <span className="w-28 shrink-0 text-muted-foreground">
                    {c.field}
                  </span>
                  <span className="text-muted-foreground">
                    {c.from} <span className="text-muted-foreground/60">→</span>{" "}
                    <span className="text-foreground">{c.to}</span>
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <aside className="lg:sticky lg:top-6">
        <Card className="py-5">
          <CardContent className="space-y-4">
            <div className="text-sm font-medium">{review.fileName}</div>
            <div className="text-sm text-muted-foreground">
              {review.changes.length} changed · {review.unknownCustomers.length}{" "}
              skipped
            </div>
            <div className="space-y-2">
              <Button className="w-full" onClick={onApply}>
                Apply changes
              </Button>
              <Button variant="ghost" className="w-full" onClick={onDiscard}>
                Discard
              </Button>
            </div>
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}
